import { getHotelsData } from '@/app/api/hotels'
import {
	DestPagesProps,
	HotelsDataType,
	PaginatedHotelsResponse,
	QueryPageParam,
	UserData,
} from '@/app/types'
import { useInfiniteQuery } from '@tanstack/react-query'

interface UseHotelsQueryProps {
	props: DestPagesProps
	startTimeRef: React.MutableRefObject<number>
	setIsFetchingComplete: (value: boolean) => void
	userData: UserData | null
}

export const useHotelsQuery = ({
	props,
	startTimeRef,
	setIsFetchingComplete,
	userData,
}: UseHotelsQueryProps) => {
	const queryKey = [
		'hotels',
		props.params.lang,
		props.params.dest.join(','),
		Object.entries(props.searchParams)
			.map(([key, value]) => `${key}=${value}`)
			.join('&'),
	]

	return useInfiniteQuery<
		PaginatedHotelsResponse,
		Error,
		HotelsDataType,
		string[],
		QueryPageParam
	>({
		queryKey,
		queryFn: ({ pageParam = { page: 1, offset: 0 } }) => {
			const updatedSearchParams = {
				...props.searchParams,
				userAgent: userData?.userAgent,
				sessionID: userData?.sessionID,
				page: String(pageParam.page),
			}
			return getHotelsData({
				...props,
				searchParams: updatedSearchParams,
			}).then((response) => {
				return {
					...response,
					page: pageParam.page,
					offset: pageParam.offset,
				}
			})
		},
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage.page < lastPage.pages) {
				return {
					page: lastPage.page + 1,
					offset: lastPage?.offset
						? lastPage?.offset + lastPage.hotels.length
						: 0,
				}
			}
			return undefined
		},
		initialPageParam: { page: 1, offset: 0 },
		staleTime: 1000 * 60 * 5,
		enabled: !!userData,
		refetchInterval: (queryInfo) => {
			if (
				queryInfo.state.data?.pages[queryInfo.state.data.pages.length - 1]
					?.complete
			) {
				setIsFetchingComplete(true)
				return false
			}

			const timeElapsed = Date.now() - startTimeRef.current
			if (timeElapsed > 20000) {
				setIsFetchingComplete(true)
				return false
			}

			return 600
		},
	})
}
